import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { BarChart3, LayoutDashboard, TrendingUp, Wallet, RotateCcw, MapPin, Package } from "lucide-react";
import { PeriodBar } from "../components/accounting/ui";
import OverviewTab from "../components/accounting/OverviewTab";
import { ProfitTab, ProductsTab, ReturnsTab } from "../components/accounting/ProductTabs";
import DebtsTab from "../components/accounting/DebtsTab";
import GeoTab from "../components/accounting/GeoTab";
import ExportButton from "../components/accounting/ExportButton";
import ExportApprovalStatus from "../components/ExportApprovalStatus";
import { damascusToday } from "../components/accounting/format";

const TABS = [
  ["overview", "نظرة عامة", LayoutDashboard, "overview"],
  ["profit", "الربح", TrendingUp, "profit"],
  ["debts", "الديون", Wallet, "debts"],
  ["returns", "المرتجعات", RotateCcw, "returns"],
  ["geo", "الأماكن", MapPin, "geo"],
  ["products", "المنتجات", Package, "products"],
];
const today = () => damascusToday();
const prevMonthStart = () => { const [y, m, d] = today().split("-").map(Number); return new Date(Date.UTC(y, m - 2, d)).toISOString().slice(0, 10); };

export default function MerchantInsights() {
  const [period, setPeriod] = useState("day");
  const [anchor, setAnchor] = useState(today());
  const [custom, setCustom] = useState({ start: prevMonthStart(), end: today() });
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [range, setRange] = useState(null);
  const [views, setViews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(() => (period === "custom" ? { period, start: custom.start, end: custom.end } : { period, date: anchor }), [period, anchor, custom]);
  const key = JSON.stringify(params);

  const load = useCallback(async (endpoint) => {
    if (period === "custom" && (!custom.start || !custom.end)) return;
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/accounting/${endpoint}`, { params });
      setData((d) => ({ ...d, [`${endpoint}:${key}`]: r.data }));
      if (endpoint === "overview") setRange(r.data.range);
    } catch (e) { setError(arabicError(e)); toast.error(arabicError(e)); } finally { setLoading(false); }
  }, [params, key, period, custom]);

  useEffect(() => { load("overview"); }, [load]);
  useEffect(() => { if (tab !== "overview") load(tab); }, [tab, load]);
  useEffect(() => { api.get("/insights/merchant").then((r) => setViews(r.data)).catch(() => {}); }, []);

  const changePeriod = (p) => { setPeriod(p); if (p !== "custom") setAnchor(today()); };
  const current = data[`${tab}:${key}`];
  const overview = data[`overview:${key}`];

  return (
    <div className="space-y-4 md:space-y-6" data-testid="insights-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333] flex items-center gap-2"><BarChart3 className="text-[#09637E]" size={32} /> إحصائياتي</h1>
          <p className="text-[#666666] mt-1 text-sm md:text-base">لوحة أعمال متجرك: المبيعات، الربح، الديون، المرتجعات، الأماكن والمنتجات — من سجلات إمداد الفعلية</p>
        </div>
        <div className="flex flex-col items-end gap-2 min-w-0 max-w-full">
          <ExportButton params={params} rangeLabel={(period === "custom" ? overview?.range : range)?.label} />
          <ExportApprovalStatus />
        </div>
      </div>

      <PeriodBar period={period} setPeriod={changePeriod} anchor={anchor} setAnchor={setAnchor} range={period === "custom" ? overview?.range : range} custom={custom} setCustom={setCustom} />

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="insights-tabs">
        {TABS.map(([k, label, Icon, tid]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${tid}`}
            className={`h-11 px-4 rounded-xl font-extrabold text-sm whitespace-nowrap flex items-center gap-1.5 border transition-colors ${tab === k ? "bg-[#09637E] text-white border-[#09637E]" : "bg-white text-[#333333] border-[#DCEBEE] hover:bg-[#F5FAFB]"}`}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {loading && !current && <div className="bg-white border border-[#DCEBEE] rounded-2xl p-10 text-center text-[#666666]" data-testid="insights-loading">جارٍ حساب الأرقام…</div>}
      {error && !current && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-6 text-center font-bold" data-testid="insights-error">{error}</div>}
      {current && (
        <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
          {tab === "overview" && <OverviewTab data={current} views={views} />}
          {tab === "profit" && <ProfitTab data={current} />}
          {tab === "debts" && <DebtsTab data={current} />}
          {tab === "returns" && <ReturnsTab data={current} />}
          {tab === "geo" && <GeoTab data={current} />}
          {tab === "products" && <ProductsTab data={current} />}
        </div>
      )}
    </div>
  );
}
